/**
 * Candidate-side Interaction Container: the ib-pwa Host Adapter.
 *
 * All candidate-state logic lives in the Assessura Renderer Binding
 * (`bind()`, @krankikom/assessura-islands). This adapter only wires it to
 * BaseObjectDirective's own save and restore triggers:
 *
 *   base fill hook    -> binding.fill(attachement)      (restore)
 *   base collect hook -> binding.collect(attachement)   (every save: autosave,
 *                        sheet blur, destroy, pagehide, visibility, freeze)
 *   element exists    -> binding.mount(host)
 *   $destroy          -> binding.destroy()
 *
 * The binding guarantees (wayfinder ticket 05): collect is synchronous, keeps
 * working after destroy, and is dirty only when the slot content differs, so no
 * trigger order can lose an answer; nothing renders before fill, so a candidate
 * cannot change anything before the stored answer arrives.
 */
import type { BindOptions, RendererBinding } from '@krankikom/assessura-islands/dist/renderer.js';
import AssessuraRenderer from '@krankikom/assessura-islands/dist/assessura-renderer.umd.js';

import BaseObjectDirective from './BaseObjectDirective';

type AssetRef = Parameters<NonNullable<BindOptions['resolveAssetUrl']>>[0];

interface InteractionContainerModel {
    getUUID(): string;
    getInteractionItem(): BindOptions['item'] | null | undefined;
}

interface MediaEntity {
    getExtendedMediaURL(taskUUID: string): string;
}

type InteractionScope = ng.IScope & {
    contentObject: InteractionContainerModel;
    questionPart?: unknown;
    isStimulus?: boolean;
    $root: ng.IRootScopeService & { isPreview?: boolean };
};

export class AsfInteractionContainerCtrl extends BaseObjectDirective {
    static readonly $inject = ['$injector', '$scope', '$element'];

    private readonly hostElement: ng.IAugmentedJQuery;
    private binding: RendererBinding | null = null;

    constructor($injector: ng.auto.IInjectorService, $scope: InteractionScope, $element: ng.IAugmentedJQuery) {
        super($injector, $scope);
        this.hostElement = $element;
        this.registerObjectInstance('interaction');

        const item = $scope.contentObject.getInteractionItem();
        if (!item) {
            console.error('[InteractionContainer] Cannot render: interactionItem is missing');
        } else if (!this.isPDFMode) {
            // PDF mode in ib-pwa is legacy code; response PDFs come from ib-main.
            this.binding = this.createBinding(item, $injector);
            const $timeout = $injector.get<ng.ITimeoutService>('$timeout');
            $timeout(() => this.mountBinding());
        }

        $scope.$on('$destroy', () => {
            // BaseObjectDirective's own destroy save has already collected
            // synchronously; collect keeps working after destroy regardless.
            this.binding?.destroy();
        });
    }

    /**
     * Restore through the fill hook immediately: skip the base's 300ms
     * "nothing registered to fill yet" wait, during which every save is dropped.
     * A getter, because the base constructor reads it before subclass fields exist.
     */
    get fillsImmediately(): boolean {
        return true;
    }

    private createBinding(item: BindOptions['item'], $injector: ng.auto.IInjectorService): RendererBinding {
        const TaskInfoService = $injector.get<{ getTaskInfo(): { getLanguageCode?(): string } | null }>('TaskInfoService');
        const uiLocale = TaskInfoService.getTaskInfo()?.getLanguageCode?.() ?? undefined;
        return AssessuraRenderer.bind({
            item,
            mode: 'interactive',
            uiLocale,
            resolveAssetUrl: (asset) => this.resolveMediaUrl(asset),
            requestSave: () => {
                void this.saveResponseData('interaction');
            },
            onError: (error, phase) => {
                console.error(`[InteractionContainer] ${phase}:`, error);
                this.recordTelemetry(`interaction.${phase}_error`, phase === 'fill' ? 'warning' : 'error', {
                    errorMessage: String(error?.message ?? error).substring(0, 200),
                });
            },
        });
    }

    /** Media Reference -> the packaged file, via the helper every other content object uses. */
    private resolveMediaUrl(asset: AssetRef): string | null {
        const mediaReference = asset.mediaReference;
        if (!mediaReference) return null;
        try {
            const media = this.TaskDataService.fetchContext().read(mediaReference) as MediaEntity | undefined;
            return media ? media.getExtendedMediaURL(this.TaskSelectionService.getTaskUUID()) : null;
        } catch {
            return null;
        }
    }

    private mountBinding(): void {
        const root = this.hostElement[0] as HTMLElement;
        const host = root.querySelector<HTMLElement>('.assessura-renderer-host') ?? root;
        this.binding?.mount(host);
    }

    /** BaseObjectDirective restore hook. */
    responseFill(attachment: unknown): void {
        this.binding?.fill(attachment);
    }

    /** BaseObjectDirective collect hook. `binding.collect` is synchronous. */
    async responseCollect(attachment: Record<string, unknown>, _triggeredBy: string): Promise<boolean> {
        return this.binding ? this.binding.collect(attachment) : false;
    }
}

export default function registerAsfInteractionContainerDirective(module: ng.IModule): void {
    module.directive('asfInteractionContainer', (): ng.IDirective => ({
        restrict: 'EA',
        replace: true,
        scope: {
            contentObject: '=',
            questionPart: '=',
        },
        templateUrl: '/views/ib/contentobjects/interaction.html',
        controller: AsfInteractionContainerCtrl,
    }));
}
